// project imports
// action - state management
import {
  CUMULATIVE_INSTALLS_REDUCER_KEY,
  CUMULATIVE_SUBSCRIBERS_REDUCER_KEY,
  SUBSCRIBER_ACQUISITION_ASSUMPTIONS_REDUCER_KEY,
} from "..";
import * as actionTypes from "../../actions/actionTypes";
import { INSTALL_KEY } from "./cumulativeInstallsReducer";
import {
  ANNUAL_CHURN_RATE_KEY,
  MONTHLY_CHURN_RATE_KEY,
  SUBSCRIPTION_KEY,
} from "../assumptions/subscriberAcquisitionAssumptionsReducer";
import { itemType } from "../types";
import { years } from "store/utils/years";
import {
  annualToTotalSubscribersMonths,
  monthlyToTotalSubscribersMonths,
  cumulativeSubscribersMonths,
  journeyToSubscriptionMonth,
} from "./staticYearsData";

// This is to filter based on company types
import { SaaSCoType, GeneralCoType } from "constants/companyTypes";

// subscriber acquisition assumption' keys
export const CUMULATIVE_SUBSCRIBERS_KEY = "cumulativeSubscribers";
export const NEW_ANNUAL_FROM_INSTALLS_KEY = "newAnnualFromInstalls";
export const NEW_ANNUAL_FROM_RENEWALS_KEY = "newAnnualFromRenewals";
export const SUBSCRIBER_FROM_INSTALL_KEY = "subscriberFromInstall";
export const SUBSCRIPTION_INSTALL_TO_SUBSCRIBER_KEY =
  "subscriptionInstallToSubscriber";
export const SUBSCRIBER_FROM_JOURNEY_KEY = "subscriberFromJourney";
export const SUBSCRIPTION_INSTALL_TO_JOURNEY_KEY =
  "subscriptionInstallToJourneys";
export const JOURNEY_TO_SUBSCRIPTION_KEY = "journeyToSubscription";
export const TOTAL_ANNUAL_SUBSCRIBERS_KEY = "totalAnnualSubscribers";
export const NEW_ANNUAL_SUBSCRIBERS_KEY = "newAnnualSubscribers";
export const ANNUAL_TO_TOTAL_SUBSCRIBERS_KEY = "annualToTotalSubscribers";
export const ANNUAL_RENEWALS_KEY = "annualRenewals";
export const CHURN_ANNUAL_SUBSCRIBERS_KEY = "churnAnnualSubscribers";
//
export const TOTAL_MONTHLY_SUBSCRIBERS_KEY = "totalMonthlySubscribers";
export const NEW_MONTHLY_SUBSCRIBERS_KEY = "newMonthlySubscribers";
export const MONTHLY_TO_TOTAL_SUBSCRIBERS_KEY = "monthlyToTotalSubscribers";
export const MONTHLY_RENEWALS_KEY = "monthlyRenewals";
export const CHURN_MONTHLY_SUBSCRIBERS_KEY = "churnMonthlySubscribers";

export const items = [
  CUMULATIVE_SUBSCRIBERS_KEY,
  NEW_ANNUAL_FROM_INSTALLS_KEY,
  NEW_ANNUAL_FROM_RENEWALS_KEY,
  SUBSCRIBER_FROM_INSTALL_KEY,
  SUBSCRIPTION_INSTALL_TO_SUBSCRIBER_KEY,
  SUBSCRIBER_FROM_JOURNEY_KEY,
  SUBSCRIPTION_INSTALL_TO_JOURNEY_KEY,
  JOURNEY_TO_SUBSCRIPTION_KEY,
  TOTAL_ANNUAL_SUBSCRIBERS_KEY,
  NEW_ANNUAL_SUBSCRIBERS_KEY,
  ANNUAL_TO_TOTAL_SUBSCRIBERS_KEY,
  ANNUAL_RENEWALS_KEY,
  CHURN_ANNUAL_SUBSCRIBERS_KEY,
  //
  TOTAL_MONTHLY_SUBSCRIBERS_KEY,
  NEW_MONTHLY_SUBSCRIBERS_KEY,
  MONTHLY_TO_TOTAL_SUBSCRIBERS_KEY,
  MONTHLY_RENEWALS_KEY,
  CHURN_MONTHLY_SUBSCRIBERS_KEY,
];

export const initialState = {
  [CUMULATIVE_SUBSCRIBERS_KEY]: {
    title: "Cumulative Subscribers",
    highlight: true,
    unit: "NUMBER",
    unitSymbol: "#",
    initialPeriod: 0,
    initialPeriodMonth: 0,
    type: itemType.CALCULATION,
    companyTypes: [SaaSCoType, GeneralCoType],
    calculate: (
      yearKey,
      getReducerFunc,
      companyType = null,
      instanceId = null
    ) => {
      const result =
        Number(
          getReducerFunc(CUMULATIVE_SUBSCRIBERS_REDUCER_KEY)[
            TOTAL_ANNUAL_SUBSCRIBERS_KEY
          ].calculate(yearKey, getReducerFunc, companyType)
        ) +
        Number(
          getReducerFunc(CUMULATIVE_SUBSCRIBERS_REDUCER_KEY)[
            TOTAL_MONTHLY_SUBSCRIBERS_KEY
          ].calculate(yearKey, getReducerFunc, companyType)
        );
      return result.toFixed(0);
    },
    months: cumulativeSubscribersMonths,
    years: {
      [years.YEAR_1_KEY]: 0,
      [years.YEAR_2_KEY]: 0,
      [years.YEAR_3_KEY]: 0,
      [years.YEAR_4_KEY]: 0,
      [years.YEAR_5_KEY]: 0,
    },
  },
  //These are no longer mean annual, the "annual" can be removed from their keys names if it wont effect any other parts
  [NEW_ANNUAL_FROM_INSTALLS_KEY]: {
    title: "New Subscribers from Installs",
    unit: "NUMBER",
    unitSymbol: "#",
    initialPeriod: 0,
    initialPeriodMonth: 0,
    type: itemType.CALCULATION,
    companyTypes: [SaaSCoType, GeneralCoType],
    calculate: (
      yearKey,
      getReducerFunc,
      companyType = null,
      instanceId = null
    ) => {
      const result =
        Number(
          getReducerFunc(CUMULATIVE_SUBSCRIBERS_REDUCER_KEY)[
            NEW_MONTHLY_SUBSCRIBERS_KEY
          ].calculate(yearKey, getReducerFunc, companyType)
        ) +
        Number(
          getReducerFunc(CUMULATIVE_SUBSCRIBERS_REDUCER_KEY)[
            NEW_ANNUAL_SUBSCRIBERS_KEY
          ].calculate(yearKey, getReducerFunc, companyType)
        );
      return result.toFixed(0);
    },
    months: cumulativeSubscribersMonths,
    years: {
      [years.YEAR_1_KEY]: 0,
      [years.YEAR_2_KEY]: 0,
      [years.YEAR_3_KEY]: 0,
      [years.YEAR_4_KEY]: 0,
      [years.YEAR_5_KEY]: 0,
    },
  },
  [NEW_ANNUAL_FROM_RENEWALS_KEY]: {
    title: "New Subscribers from Renewals",
    unit: "NUMBER",
    unitSymbol: "#",
    initialPeriod: 0,
    initialPeriodMonth: 0,
    type: itemType.CALCULATION,
    companyTypes: [SaaSCoType, GeneralCoType],
    calculate: (
      yearKey,
      getReducerFunc,
      companyType = null,
      instanceId = null
    ) => {
      const result =
        Number(
          getReducerFunc(CUMULATIVE_SUBSCRIBERS_REDUCER_KEY)[
            ANNUAL_RENEWALS_KEY
          ].calculate(yearKey, getReducerFunc, companyType)
        ) +
        Number(
          getReducerFunc(CUMULATIVE_SUBSCRIBERS_REDUCER_KEY)[
            MONTHLY_RENEWALS_KEY
          ].calculate(yearKey, getReducerFunc, companyType)
        );
      return result.toFixed(0);
    },
    months: cumulativeSubscribersMonths,
    years: {
      [years.YEAR_1_KEY]: 0,
      [years.YEAR_2_KEY]: 0,
      [years.YEAR_3_KEY]: 0,
      [years.YEAR_4_KEY]: 0,
      [years.YEAR_5_KEY]: 0,
    },
  },
  [SUBSCRIBER_FROM_INSTALL_KEY]: {
    title: "Subscriber from Installs",
    unit: "NUMBER",
    unitSymbol: "#",
    initialPeriod: 0,
    initialPeriodMonth: 0,
    showInitialPeriod: true,
    type: itemType.CALCULATION,
    companyTypes: [SaaSCoType, GeneralCoType],
    calculate: (
      yearKey,
      getReducerFunc,
      companyType = null,
      instanceId = null
    ) => {
      const result =
        (getReducerFunc(CUMULATIVE_INSTALLS_REDUCER_KEY)[INSTALL_KEY].calculate(
          yearKey,
          getReducerFunc
        ) *
          getReducerFunc(SUBSCRIBER_ACQUISITION_ASSUMPTIONS_REDUCER_KEY)[
            SUBSCRIPTION_KEY
          ]["years"][yearKey]) /
        100;
      return result.toFixed(0);
    },
    months: cumulativeSubscribersMonths,
    years: {
      [years.YEAR_1_KEY]: 0,
      [years.YEAR_2_KEY]: 0,
      [years.YEAR_3_KEY]: 0,
      [years.YEAR_4_KEY]: 0,
      [years.YEAR_5_KEY]: 0,
    },
  },
  [SUBSCRIPTION_INSTALL_TO_SUBSCRIBER_KEY]: {
    title: "Subscription (install to subscriber)",
    unit: "PERCENTAGE",
    unitSymbol: "%",
    initialPeriod: 0,
    initialPeriodMonth: 0,
    type: itemType.CALCULATION,
    companyTypes: [SaaSCoType, GeneralCoType],
    calculate: (
      yearKey,
      getReducerFunc,
      companyType = null,
      instanceId = null
    ) => {
      return getReducerFunc(SUBSCRIBER_ACQUISITION_ASSUMPTIONS_REDUCER_KEY)[
        SUBSCRIPTION_KEY
      ]["years"][yearKey];
    },
    months: cumulativeSubscribersMonths,
    years: {
      [years.YEAR_1_KEY]: 0,
      [years.YEAR_2_KEY]: 0,
      [years.YEAR_3_KEY]: 0,
      [years.YEAR_4_KEY]: 0,
      [years.YEAR_5_KEY]: 0,
    },
  },
  [SUBSCRIBER_FROM_JOURNEY_KEY]: {
    title: "Subscriber from Journeys",
    unit: "NUMBER",
    unitSymbol: "#",
    initialPeriod: 0,
    initialPeriodMonth: 0,
    showInitialPeriod: true,
    type: itemType.CALCULATION,
    companyTypes: [SaaSCoType, GeneralCoType],
    calculate: (
      yearKey,
      getReducerFunc,
      companyType = null,
      instanceId = null
    ) => {
      const installJourneys = getReducerFunc(
        CUMULATIVE_SUBSCRIBERS_REDUCER_KEY
      )[SUBSCRIPTION_INSTALL_TO_JOURNEY_KEY].calculate(
        yearKey,
        getReducerFunc,
        companyType
      );

      let previousCumulativeInstall = getReducerFunc(
        CUMULATIVE_INSTALLS_REDUCER_KEY
      )[INSTALL_KEY]["initialPeriod"];

      let previousSubscriberFromInstall = getReducerFunc(
        CUMULATIVE_SUBSCRIBERS_REDUCER_KEY
      )[SUBSCRIBER_FROM_INSTALL_KEY]["initialPeriod"];

      if (yearKey > 1) {
        previousCumulativeInstall = getReducerFunc(
          CUMULATIVE_INSTALLS_REDUCER_KEY
        )[INSTALL_KEY].calculate(yearKey - 1, getReducerFunc);

        previousSubscriberFromInstall = getReducerFunc(
          CUMULATIVE_SUBSCRIBERS_REDUCER_KEY
        )[SUBSCRIBER_FROM_INSTALL_KEY].calculate(yearKey - 1, getReducerFunc);
      }

      const result =
        (installJourneys *
          (previousCumulativeInstall - previousSubscriberFromInstall)) /
        100;

      return result.toFixed(0);
    },
    months: cumulativeSubscribersMonths,
    years: {
      [years.YEAR_1_KEY]: 0,
      [years.YEAR_2_KEY]: 0,
      [years.YEAR_3_KEY]: 0,
      [years.YEAR_4_KEY]: 0,
      [years.YEAR_5_KEY]: 0,
    },
  },
  [SUBSCRIPTION_INSTALL_TO_JOURNEY_KEY]: {
    title: "Subscription (install to journey)",
    unit: "PERCENTAGE",
    unitSymbol: "%",
    initialPeriod: 0,
    initialPeriodMonth: 0,
    type: itemType.CALCULATION,
    companyTypes: [SaaSCoType, GeneralCoType],
    calculate: (
      yearKey,
      getReducerFunc,
      companyType = null,
      instanceId = null
    ) => {
      return (
        (getReducerFunc(CUMULATIVE_SUBSCRIBERS_REDUCER_KEY)[
          SUBSCRIPTION_INSTALL_TO_SUBSCRIBER_KEY
        ].calculate(yearKey, getReducerFunc, companyType) *
          getReducerFunc(CUMULATIVE_SUBSCRIBERS_REDUCER_KEY)[
            JOURNEY_TO_SUBSCRIPTION_KEY
          ]["years"][yearKey]) /
        100
      );
    },
    months: cumulativeSubscribersMonths,
    years: {
      [years.YEAR_1_KEY]: 0,
      [years.YEAR_2_KEY]: 0,
      [years.YEAR_3_KEY]: 0,
      [years.YEAR_4_KEY]: 0,
      [years.YEAR_5_KEY]: 0,
    },
  },
  [JOURNEY_TO_SUBSCRIPTION_KEY]: {
    title: "Journey to Subscription Ratio",
    unit: "PERCENTAGE",
    unitSymbol: "%",
    initialPeriod: 0,
    initialPeriodMonth: 0,
    type: itemType.INPUT,
    companyTypes: [SaaSCoType, GeneralCoType],
    calculate: (
      yearKey,
      getReducerFunc,
      companyType = null,
      instanceId = null
    ) => {
      return 0;
    },
    months: journeyToSubscriptionMonth,
    years: {
      [years.YEAR_1_KEY]: 50,
      [years.YEAR_2_KEY]: 50,
      [years.YEAR_3_KEY]: 50,
      [years.YEAR_4_KEY]: 50,
      [years.YEAR_5_KEY]: 50,
    },
  },
  [TOTAL_ANNUAL_SUBSCRIBERS_KEY]: {
    title: "Total Annual Subscribers",
    unit: "NUMBER",
    highlight: true,
    unitSymbol: "#",
    initialPeriod: 0,
    initialPeriodMonth: 0,
    type: itemType.CALCULATION,
    companyTypes: [SaaSCoType, GeneralCoType],
    calculate: (
      yearKey,
      getReducerFunc,
      companyType = null,
      instanceId = null
    ) => {
      const result =
        Number(
          getReducerFunc(CUMULATIVE_SUBSCRIBERS_REDUCER_KEY)[
            NEW_ANNUAL_SUBSCRIBERS_KEY
          ].calculate(yearKey, getReducerFunc, companyType)
        ) +
        Number(
          getReducerFunc(CUMULATIVE_SUBSCRIBERS_REDUCER_KEY)[
            ANNUAL_RENEWALS_KEY
          ].calculate(yearKey, getReducerFunc, companyType)
        );
      return result.toFixed(0);
    },
    months: cumulativeSubscribersMonths,
    years: {
      [years.YEAR_1_KEY]: 0,
      [years.YEAR_2_KEY]: 0,
      [years.YEAR_3_KEY]: 0,
      [years.YEAR_4_KEY]: 0,
      [years.YEAR_5_KEY]: 0,
    },
  },
  [NEW_ANNUAL_SUBSCRIBERS_KEY]: {
    title: "New Annual Subscribers",
    unit: "NUMBER",
    unitSymbol: "#",
    initialPeriod: 0,
    initialPeriodMonth: 0,
    type: itemType.CALCULATION,
    companyTypes: [SaaSCoType, GeneralCoType],
    calculate: (
      yearKey,
      getReducerFunc,
      companyType = null,
      instanceId = null
    ) => {
      const result =
        ((Number(
          getReducerFunc(CUMULATIVE_SUBSCRIBERS_REDUCER_KEY)[
            SUBSCRIBER_FROM_INSTALL_KEY
          ].calculate(yearKey, getReducerFunc, companyType)
        ) +
          Number(
            getReducerFunc(CUMULATIVE_SUBSCRIBERS_REDUCER_KEY)[
              SUBSCRIBER_FROM_JOURNEY_KEY
            ].calculate(yearKey, getReducerFunc, companyType)
          )) *
          getReducerFunc(CUMULATIVE_SUBSCRIBERS_REDUCER_KEY)[
            ANNUAL_TO_TOTAL_SUBSCRIBERS_KEY
          ]["years"][yearKey]) /
        100;
      return result.toFixed(0);
    },
    months: cumulativeSubscribersMonths,
    years: {
      [years.YEAR_1_KEY]: 0,
      [years.YEAR_2_KEY]: 0,
      [years.YEAR_3_KEY]: 0,
      [years.YEAR_4_KEY]: 0,
      [years.YEAR_5_KEY]: 0,
    },
  },
  [ANNUAL_TO_TOTAL_SUBSCRIBERS_KEY]: {
    title: "Annual Subs. / Total Subs.",
    unit: "PERCENTAGE",
    unitSymbol: "%",
    initialPeriod: 0,
    initialPeriodMonth: 0,
    type: itemType.INPUT,
    companyTypes: [SaaSCoType, GeneralCoType],
    calculate: (
      yearKey,
      getReducerFunc,
      companyType = null,
      instanceId = null
    ) => {
      return 0;
    },
    months: annualToTotalSubscribersMonths,
    years: {
      [years.YEAR_1_KEY]: 60,
      [years.YEAR_2_KEY]: 60,
      [years.YEAR_3_KEY]: 60,
      [years.YEAR_4_KEY]: 60,
      [years.YEAR_5_KEY]: 60,
    },
  },
  [ANNUAL_RENEWALS_KEY]: {
    title: "Number of Annual Renewals",
    unit: "NUMBER",
    unitSymbol: "#",
    initialPeriod: 0,
    initialPeriodMonth: 0,
    type: itemType.CALCULATION,
    companyTypes: [SaaSCoType, GeneralCoType],
    calculate: (
      yearKey,
      getReducerFunc,
      companyType = null,
      instanceId = null
    ) => {
      let previousAnnualRenewals = getReducerFunc(
        CUMULATIVE_SUBSCRIBERS_REDUCER_KEY
      )[ANNUAL_RENEWALS_KEY]["initialPeriod"];

      if (yearKey > 1) {
        previousAnnualRenewals = getReducerFunc(
          CUMULATIVE_SUBSCRIBERS_REDUCER_KEY
        )[ANNUAL_RENEWALS_KEY].calculate(yearKey - 1, getReducerFunc);
      }

      let previousAnnualSubscribers = getReducerFunc(
        CUMULATIVE_SUBSCRIBERS_REDUCER_KEY
      )[NEW_ANNUAL_SUBSCRIBERS_KEY]["initialPeriod"];

      if (yearKey > 1) {
        previousAnnualSubscribers = getReducerFunc(
          CUMULATIVE_SUBSCRIBERS_REDUCER_KEY
        )[NEW_ANNUAL_SUBSCRIBERS_KEY].calculate(yearKey - 1, getReducerFunc);
      }

      let churn = getReducerFunc(CUMULATIVE_SUBSCRIBERS_REDUCER_KEY)[
        CHURN_ANNUAL_SUBSCRIBERS_KEY
      ].calculate(yearKey, getReducerFunc, companyType);

      const result =
        ((Number(previousAnnualRenewals) + Number(previousAnnualSubscribers)) *
          (100 - churn)) /
        100;

      return Number.isNaN(result) ? 0 : result.toFixed(0);
    },
    months: cumulativeSubscribersMonths,
    years: {
      [years.YEAR_1_KEY]: 0,
      [years.YEAR_2_KEY]: 0,
      [years.YEAR_3_KEY]: 0,
      [years.YEAR_4_KEY]: 0,
      [years.YEAR_5_KEY]: 0,
    },
  },
  [CHURN_ANNUAL_SUBSCRIBERS_KEY]: {
    title: "Churn Rate of Annual Subs.",
    unit: "PERCENTAGE",
    unitSymbol: "%",
    initialPeriod: 0,
    initialPeriodMonth: 0,
    type: itemType.CALCULATION,
    companyTypes: [SaaSCoType, GeneralCoType],
    calculate: (
      yearKey,
      getReducerFunc,
      companyType = null,
      instanceId = null
    ) => {
      return getReducerFunc(SUBSCRIBER_ACQUISITION_ASSUMPTIONS_REDUCER_KEY)[
        ANNUAL_CHURN_RATE_KEY
      ]["years"][yearKey];
    },
    months: cumulativeSubscribersMonths,
    years: {
      [years.YEAR_1_KEY]: 0,
      [years.YEAR_2_KEY]: 0,
      [years.YEAR_3_KEY]: 0,
      [years.YEAR_4_KEY]: 0,
      [years.YEAR_5_KEY]: 0,
    },
  },
  //__________________________________________________________________________
  [TOTAL_MONTHLY_SUBSCRIBERS_KEY]: {
    title: "Total Monthly Subscribers",
    unit: "NUMBER",
    highlight: true,
    unitSymbol: "#",
    initialPeriod: 0,
    initialPeriodMonth: 0,
    type: itemType.CALCULATION,
    companyTypes: [SaaSCoType, GeneralCoType],
    calculate: (
      yearKey,
      getReducerFunc,
      companyType = null,
      instanceId = null
    ) => {
      const result =
        Number(
          getReducerFunc(CUMULATIVE_SUBSCRIBERS_REDUCER_KEY)[
            NEW_MONTHLY_SUBSCRIBERS_KEY
          ].calculate(yearKey, getReducerFunc, companyType)
        ) +
        Number(
          getReducerFunc(CUMULATIVE_SUBSCRIBERS_REDUCER_KEY)[
            MONTHLY_RENEWALS_KEY
          ].calculate(yearKey, getReducerFunc, companyType)
        );
      return result.toFixed(0);
    },
    months: cumulativeSubscribersMonths,
    years: {
      [years.YEAR_1_KEY]: 0,
      [years.YEAR_2_KEY]: 0,
      [years.YEAR_3_KEY]: 0,
      [years.YEAR_4_KEY]: 0,
      [years.YEAR_5_KEY]: 0,
    },
  },
  [NEW_MONTHLY_SUBSCRIBERS_KEY]: {
    title: "New Monthly Subscribers",
    unit: "NUMBER",
    unitSymbol: "#",
    initialPeriod: 0,
    initialPeriodMonth: 0,
    type: itemType.CALCULATION,
    companyTypes: [SaaSCoType, GeneralCoType],
    calculate: (
      yearKey,
      getReducerFunc,
      companyType = null,
      instanceId = null
    ) => {
      const result =
        ((Number(
          getReducerFunc(CUMULATIVE_SUBSCRIBERS_REDUCER_KEY)[
            SUBSCRIBER_FROM_INSTALL_KEY
          ].calculate(yearKey, getReducerFunc, companyType)
        ) +
          Number(
            getReducerFunc(CUMULATIVE_SUBSCRIBERS_REDUCER_KEY)[
              SUBSCRIBER_FROM_JOURNEY_KEY
            ].calculate(yearKey, getReducerFunc, companyType)
          )) *
          getReducerFunc(CUMULATIVE_SUBSCRIBERS_REDUCER_KEY)[
            MONTHLY_TO_TOTAL_SUBSCRIBERS_KEY
          ]["years"][yearKey]) /
        100;
      return result.toFixed(0);
    },
    months: cumulativeSubscribersMonths,
    years: {
      [years.YEAR_1_KEY]: 0,
      [years.YEAR_2_KEY]: 0,
      [years.YEAR_3_KEY]: 0,
      [years.YEAR_4_KEY]: 0,
      [years.YEAR_5_KEY]: 0,
    },
  },
  [MONTHLY_TO_TOTAL_SUBSCRIBERS_KEY]: {
    title: "Monthly Subs. / Total Subs.",
    unit: "PERCENTAGE",
    unitSymbol: "%",
    initialPeriod: 0,
    initialPeriodMonth: 0,
    type: itemType.INPUT,
    companyTypes: [SaaSCoType, GeneralCoType],
    calculate: (
      yearKey,
      getReducerFunc,
      companyType = null,
      instanceId = null
    ) => {
      return 0;
    },
    months: monthlyToTotalSubscribersMonths,
    years: {
      [years.YEAR_1_KEY]: 40,
      [years.YEAR_2_KEY]: 40,
      [years.YEAR_3_KEY]: 40,
      [years.YEAR_4_KEY]: 40,
      [years.YEAR_5_KEY]: 40,
    },
  },
  [MONTHLY_RENEWALS_KEY]: {
    title: "Number of Monthly Renewals",
    unit: "NUMBER",
    unitSymbol: "#",
    initialPeriod: 0,
    initialPeriodMonth: 0,
    type: itemType.CALCULATION,
    companyTypes: [SaaSCoType, GeneralCoType],
    calculate: (
      yearKey,
      getReducerFunc,
      companyType = null,
      instanceId = null
    ) => {
      let previousMonthlyRenewals = getReducerFunc(
        CUMULATIVE_SUBSCRIBERS_REDUCER_KEY
      )[MONTHLY_RENEWALS_KEY]["initialPeriod"];

      if (yearKey > 1) {
        previousMonthlyRenewals = getReducerFunc(
          CUMULATIVE_SUBSCRIBERS_REDUCER_KEY
        )[MONTHLY_RENEWALS_KEY].calculate(yearKey - 1, getReducerFunc);
      }

      let previousMonthlySubscribers = getReducerFunc(
        CUMULATIVE_SUBSCRIBERS_REDUCER_KEY
      )[NEW_MONTHLY_SUBSCRIBERS_KEY]["initialPeriod"];

      if (yearKey > 1) {
        previousMonthlySubscribers = getReducerFunc(
          CUMULATIVE_SUBSCRIBERS_REDUCER_KEY
        )[NEW_MONTHLY_SUBSCRIBERS_KEY].calculate(yearKey - 1, getReducerFunc);
      }

      let churn = getReducerFunc(CUMULATIVE_SUBSCRIBERS_REDUCER_KEY)[
        CHURN_MONTHLY_SUBSCRIBERS_KEY
      ].calculate(yearKey, getReducerFunc, companyType);

      const result =
        ((Number(previousMonthlyRenewals) +
          Number(previousMonthlySubscribers)) *
          (100 - churn)) /
        100;

      return Number.isNaN(result) ? 0 : result.toFixed(0);
    },
    months: cumulativeSubscribersMonths,
    years: {
      [years.YEAR_1_KEY]: 0,
      [years.YEAR_2_KEY]: 0,
      [years.YEAR_3_KEY]: 0,
      [years.YEAR_4_KEY]: 0,
      [years.YEAR_5_KEY]: 0,
    },
  },
  [CHURN_MONTHLY_SUBSCRIBERS_KEY]: {
    title: "Churn Rate of Monthly Subs.",
    unit: "PERCENTAGE",
    unitSymbol: "%",
    initialPeriod: 0,
    initialPeriodMonth: 0,
    type: itemType.CALCULATION,
    companyTypes: [SaaSCoType, GeneralCoType],
    calculate: (
      yearKey,
      getReducerFunc,
      companyType = null,
      instanceId = null
    ) => {
      return getReducerFunc(SUBSCRIBER_ACQUISITION_ASSUMPTIONS_REDUCER_KEY)[
        MONTHLY_CHURN_RATE_KEY
      ]["years"][yearKey];
    },
    months: cumulativeSubscribersMonths,
    years: {
      [years.YEAR_1_KEY]: 0,
      [years.YEAR_2_KEY]: 0,
      [years.YEAR_3_KEY]: 0,
      [years.YEAR_4_KEY]: 0,
      [years.YEAR_5_KEY]: 0,
    },
  },
};

// ==============================|| SUBSCRIPTION ACQUISITION ASSUMPTIONS REDUCER ||============================== //

const cumulativeSubscribersReducer = (state = initialState, action) => {
  switch (action.type) {
    case actionTypes.SET_CUMULATIVE_SUBSCRIBER_BY_YEAR:
      return {
        ...state,
        [action.key]: {
          ...state[action.key],
          years: {
            ...state[action.key].years,
            [action.yearKey]: [action.amount],
          },
        },
      };
    case actionTypes.SET_CUMULATIVE_SUBSCRIBER_BY_MONTH:
      return {
        ...state,
        [action.key]: {
          ...state[action.key],
          months: {
            ...state[action.key].months,
            [action.yearKey]: [action.amount],
          },
        },
      };
    case actionTypes.REPLACE_CUMULATIVE_SUBSCRIBER_YEARS:
      return {
        ...state,
        [action.key]: {
          ...state[action.key],
          months: action?.payload,
        },
      };
    case actionTypes.RESET_CUMULATIVE_SUBSCRIBER_REDUCER:
      return initialState;
    default:
      return state;
  }
};

export default cumulativeSubscribersReducer;
